import React, { useRef, useEffect, useCallback, useState } from "react";
import { motion } from "framer-motion";
import { Alert, Paper, TextField, Box, useTheme } from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  useScanOrderItem,
  useConfirmOrderItem
} from "../../../hooks/api/useSalesOrder";
import { useBarcodeScanner } from "../../../hooks/useBarcodeScanner";
import QuantityConfirmModal from "./QuantityConfirmModal";
import MeasurementConfirmModal from "./MeasurementConfirmModal";
import {
  BRAND_COLOR,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import { useReportError } from "../../../hooks/useReportError";
import { OrderStatus } from "../enums/orderStatus";
import { OrderMutationErrorCode } from "../enums/orderMutationErrorCode";
import { resolvePostScanFlow } from "../utils/scanResponseUtils";
import BrandButton from "../../common/BrandButton/BrandButton";
import { SCANABLE_STATUSES } from "../constants/barcodeScannerConstants";
import { isEnterKey } from "../utils/keyboardUtils";
import type {
  PendingConfirm,
  OrderBarcodeScannerProps
} from "../interfaces/barcodeScanner";

export type { OrderBarcodeScannerProps };

const OrderBarcodeScanner: React.FC<OrderBarcodeScannerProps> = ({
  orderId,
  orderStatus,
  onRefetch,
  disabled = false,
  placeholder = "Scan barcode here (or type and press Enter)",
  sx
}) => {
  const { t } = useKivunovaTranslation();
  const reportError = useReportError();
  const scanInputRef = useRef<HTMLInputElement>(null);
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));

  const [scannerBanner, setScannerBanner] = useState<string | null>(null);

  const onScanRejectedCode = useCallback(
    (code: string) => {
      if (
        code === OrderMutationErrorCode.INVALID_STATE_TRANSITION ||
        code === OrderMutationErrorCode.ALREADY_RESERVED
      ) {
        setScannerBanner(
          t(
            "order-error-invalid-state-scan-hint",
            "You cannot scan items onto this order in its current state. Open a draft order to add products, or refresh and try again."
          )
        );
      } else if (code === OrderMutationErrorCode.DUPLICATE_ERROR) {
        setScannerBanner(
          t(
            "order-error-duplicate-conflict-scan-hint",
            "Inventory reported a conflict (duplicate or concurrent change). Refresh the order and try again."
          )
        );
      } else if (code === OrderMutationErrorCode.CONCURRENCY_ERROR) {
        setScannerBanner(
          t(
            "order-error-concurrency-scan-hint",
            "This order or inventory changed elsewhere. Refresh and try again."
          )
        );
      } else {
        setScannerBanner(null);
      }
    },
    [t]
  );

  const scanOrderItem = useScanOrderItem({
    onRejectedCode: onScanRejectedCode
  });
  const confirmOrderItem = useConfirmOrderItem();

  const [pendingConfirm, setPendingConfirm] =
    React.useState<PendingConfirm | null>(null);

  const isScanningEnabled =
    !!orderId &&
    SCANABLE_STATUSES.includes(orderStatus as OrderStatus) &&
    !disabled;

  const handleScan = useCallback(
    async (barcode: string) => {
      if (!orderId || !isScanningEnabled) return;
      setScannerBanner(null);
      try {
        const scan = await scanOrderItem.mutateAsync({
          orderId,
          productItemId: barcode.trim()
        });
        if (!scan) return;

        setScannerBanner(null);
        const flow = resolvePostScanFlow(scan);
        if (flow.action === "refetch") {
          await onRefetch?.();
        } else if (
          flow.action === "quantity_modal" ||
          flow.action === "measurement_modal"
        ) {
          setPendingConfirm({
            productItemId: scan.productItemId ?? "",
            productName:
              scan.variationName?.trim() ||
              scan.productName?.trim() ||
              scan.orderLine?.variationName?.trim() ||
              scan.orderLine?.productName?.trim() ||
              scan.productItemId ||
              scan.orderLine?.productItemId ||
              "",
            remainingQuantity: Number(scan.remainingQuantity ?? 0),
            unit: scan.unit ?? "",
            isMeasurement: flow.action === "measurement_modal",
            measureType: scan.measureType
          });
        }
      } catch (err) {
        reportError("Order barcode scan flow failed", err, {
          component: "OrderBarcodeScanner",
          orderId: orderId ?? ""
        });
      }
    },
    [orderId, isScanningEnabled, scanOrderItem, onRefetch, reportError]
  );

  useBarcodeScanner(handleScan, {
    enabled: isScanningEnabled
  });

  useEffect(() => {
    if (isScanningEnabled && !pendingConfirm && scanInputRef.current) {
      const t = setTimeout(() => scanInputRef.current?.focus(), 100);
      return () => clearTimeout(t);
    }
  }, [isScanningEnabled, pendingConfirm]);

  const handleConfirmQuantity = useCallback(
    async (quantity: number) => {
      if (!orderId || !pendingConfirm) return;
      try {
        await confirmOrderItem.mutateAsync({
          orderId,
          productItemId: pendingConfirm.productItemId,
          quantity
        });
        setPendingConfirm(null);
        await onRefetch?.();
      } catch (err) {
        reportError("Order barcode confirm quantity failed", err, {
          component: "OrderBarcodeScanner",
          orderId: orderId ?? ""
        });
      }
    },
    [orderId, pendingConfirm, confirmOrderItem, onRefetch, reportError]
  );

  const processBarcode = useCallback(
    (input: HTMLInputElement) => {
      const value = input.value?.trim();
      if (value) {
        handleScan(value);
        input.value = "";
      }
    },
    [handleScan]
  );

  if (!isScanningEnabled) return null;

  return (
    <>
      {scannerBanner && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          onClose={() => setScannerBanner(null)}
        >
          {scannerBanner}
        </Alert>
      )}
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Paper
          component={motion.div}
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          elevation={0}
          sx={{
            display: "flex",
            alignItems: "center",
            p: 0,
            width: matchMD ? "100%" : "500px",
            borderRadius: 3,
            border: "1px solid",
            borderColor: `${BRAND_COLOR}30`,
            overflow: "hidden",
            backgroundColor: "background.paper",
            boxShadow: `0 2px 12px ${BRAND_COLOR}08`,
            transition: "box-shadow 0.2s ease, border-color 0.2s ease",
            "&:focus-within": {
              borderColor: BRAND_COLOR,
              boxShadow: `0 2px 16px ${BRAND_COLOR}15, 0 0 0 2px ${BRAND_COLOR}25`
            },
            ...sx
          }}
        >
          <TextField
            inputRef={scanInputRef}
            variant="standard"
            placeholder={placeholder}
            autoComplete="off"
            hiddenLabel
            data-testid="sales-order-barcode-input"
            inputProps={{
              "aria-label": "Barcode scan input",
              onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => {
                e.stopPropagation();
                const input = e.target as HTMLInputElement;
                if (isEnterKey(e)) {
                  e.preventDefault();
                  processBarcode(input);
                }
              }
            }}
            slotProps={{
              input: {
                sx: {
                  py: 1.5,
                  px: 2,
                  "&::placeholder": { opacity: 0.6 },
                  "&:focus": { outline: "none" }
                }
              }
            }}
            sx={{
              flex: 1,
              "& .MuiInputBase-root": { borderRadius: 0 },
              "& .MuiInputBase-root:before, & .MuiInputBase-root:after": {
                display: "none"
              }
            }}
          />
          <BrandButton
            size="small"
            onClick={() => {
              const input = scanInputRef.current;
              if (input) processBarcode(input);
            }}
            sx={{ m: 0.75 }}
            data-testid="sales-order-btn-barcode-add-item"
          >
            Add Item
          </BrandButton>
        </Paper>
      </Box>

      {pendingConfirm && !pendingConfirm.isMeasurement && (
        <QuantityConfirmModal
          open
          onClose={() => setPendingConfirm(null)}
          onConfirm={handleConfirmQuantity}
          productName={pendingConfirm.productName}
          remainingQuantity={pendingConfirm.remainingQuantity}
          unit={pendingConfirm.unit}
          measureType={pendingConfirm.measureType}
          isLoading={confirmOrderItem.isPending}
        />
      )}
      {pendingConfirm && pendingConfirm.isMeasurement && (
        <MeasurementConfirmModal
          open
          onClose={() => setPendingConfirm(null)}
          onConfirm={handleConfirmQuantity}
          productName={pendingConfirm.productName}
          remainingQuantity={pendingConfirm.remainingQuantity}
          unit={pendingConfirm.unit}
          measureType={pendingConfirm.measureType}
          isLoading={confirmOrderItem.isPending}
        />
      )}
    </>
  );
};

export default OrderBarcodeScanner;
